/**
 * Jarvis Details Component
 * 
 * Displays detailed information about the Jarvis auto-remediation
 * system, including status and recent incidents.
 */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bot } from "lucide-react";

export function JarvisDetails({ adminAxios, jarvis }) {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchIncidents = async () => {
      try {
        const res = await adminAxios.get("/command-center/jarvis/incidents?limit=20");
        setIncidents(res.data.incidents || []);
      } catch (err) {
        console.error("Error fetching incidents:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchIncidents();
  }, [adminAxios]);

  return (
    <div className="space-y-4">
      {/* Jarvis Status Card */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-cyan-400" />
            Jarvis - Sistema de Auto-Remediacao
          </CardTitle>
          <CardDescription>
            Monitora o sistema e aplica correcoes automaticas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-muted/30 rounded-lg">
              <p className="text-2xl font-bold">{jarvis.stats?.scans_today || 0}</p>
              <p className="text-xs text-muted-foreground">Scans Hoje</p>
            </div>
            <div className="text-center p-4 bg-muted/30 rounded-lg">
              <p className="text-2xl font-bold">{jarvis.stats?.incidents_today || 0}</p>
              <p className="text-xs text-muted-foreground">Problemas</p>
            </div>
            <div className="text-center p-4 bg-green-500/10 rounded-lg">
              <p className="text-2xl font-bold text-green-400">{jarvis.stats?.fixes_today || 0}</p>
              <p className="text-xs text-muted-foreground">Corrigidos</p>
            </div>
            <div className="text-center p-4 bg-red-500/10 rounded-lg">
              <p className="text-2xl font-bold text-red-400">{jarvis.stats?.failures_today || 0}</p>
              <p className="text-xs text-muted-foreground">Falhas</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Incidents */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Incidents Recentes</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-4">Carregando...</p>
          ) : incidents.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">Nenhum incident recente</p>
          ) : (
            <div className="space-y-2">
              {incidents.map((inc, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant={inc.result === "auto_resolved" ? "default" : "destructive"}>
                      {inc.result === "auto_resolved" ? "OK" : inc.result}
                    </Badge>
                    <div>
                      <p className="font-medium">{inc.rule}</p>
                      <p className="text-xs text-muted-foreground">{inc.summary}</p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(inc.timestamp).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
